/* eslint-disable no-restricted-syntax,@typescript-eslint/no-unused-vars,@typescript-eslint/no-explicit-any,no-param-reassign,max-classes-per-file,class-methods-use-this */
import { calculate } from './calculate';

type LogMetadata = Record<string, any>;

export type SendLog = (message: string, metadata?: LogMetadata) => void;

const generateRandomId = () => {
  return Math.random().toString(36).substring(2);
};

/**
 * Service to measure the performance of a function
 *
 * <b>Remember to call dispose when the service is no longer needed</b>
 */
export class MeasurePerformanceService {
  private send: SendLog | undefined;

  private timeoutInterval: number | undefined;

  private sendTimesInterval: number | undefined;

  public readonly prefix: string;

  private threshold: number = 0;

  private timesPerLabel = new Map<
    string,
    {
      start: number;
      startMetadata?: LogMetadata;
      diff?: number;
      endMetadata?: LogMetadata;
    }
  >();

  constructor(
    prefix: string,
    sendLog?: SendLog,
    options?: {
      timeout?: number;
      threshold?: number;
    },
  ) {
    this.prefix = prefix;

    if (sendLog) {
      this.init(sendLog, options);
    }
  }

  /**
   * Initializes the MeasurePerformanceService if you didn't pass it in the constructor
   * @param sendLog
   * @param options
   * @param options.timeout Timeout in ms to send a log if the diff is greater than it
   * @param options.threshold Threshold in ms to send the log if the diff is greater or equal to it
   */
  init(
    sendLog: SendLog,
    options?: {
      timeout?: number;
      threshold?: number;
    },
  ) {
    const timeout = options?.timeout ?? 10000;
    this.threshold = options?.threshold ?? 0;

    this.send = sendLog;

    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }

    this.timeoutInterval = setInterval(() => {
      const times = Array.from(this.timesPerLabel.entries()).filter(
        v => v[1].diff === undefined,
      );

      for (const [fullLabel, { start }] of times) {
        const diff = Date.now() - start;

        const [label] = fullLabel.split('-');

        if (diff > timeout) {
          this.sendLog(`[PerformanceTimeTimeout] Timeout for ${label}: ${diff}ms`, {
            label,
            diff,
          });

          this.timesPerLabel.delete(fullLabel);
        }
      }
    }, 5000) as unknown as number;

    if (this.sendTimesInterval) {
      clearInterval(this.sendTimesInterval);
    }

    this.sendTimesInterval = setInterval(
      this.sendTimes.bind(this),
      10000,
    ) as unknown as number;
  }

  private sendLog(message: string, metadata?: LogMetadata) {
    this.send?.(`[${this.prefix}] ${message}`, {
      ...metadata,
    });

    if (!this.send) {
      console.warn(
        `No send function defined for MeasurePerformanceService (${this.prefix})`,
      );
    }
  }

  private sendTimes() {
    const times = Array.from(this.timesPerLabel.entries()).filter(
      v => v[1].diff !== undefined,
    );

    const ungrupableTimes = times.filter(
      ([_, { startMetadata, endMetadata }]) =>
        startMetadata !== undefined && endMetadata !== undefined,
    );

    const grupableTimes = times.filter(
      ([_, { startMetadata, endMetadata }]) =>
        startMetadata === undefined || endMetadata === undefined,
    );

    const groupedByLabelTimes = grupableTimes.reduce((acc, [label, { start, diff }]) => {
      const current = acc.get(label) || { start, diff: 0 };

      if (diff === undefined) {
        return acc;
      }

      const newDiff = calculate(() => {
        if (current.diff === 0) {
          return diff;
        }

        return (current.diff + diff) / 2;
      });

      acc.set(label, { start, diff: newDiff });

      return acc;
    }, new Map<string, { start: number; diff: number }>());

    for (const [label, { diff }] of [
      ...ungrupableTimes,
      ...Array.from(groupedByLabelTimes.entries()),
    ]) {
      if (diff !== undefined && diff >= this.threshold) {
        this.sendLog(`[PerformanceTime] Time for ${label}: ${diff}ms`, {
          label,
          diff,
        });
      }

      this.timesPerLabel.delete(label);
    }
  }

  /**
   * Disposes the MeasurePerformanceService
   */
  dispose() {
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval);
    }

    if (this.sendTimesInterval) {
      clearInterval(this.sendTimesInterval);
    }

    this.sendTimes();
  }

  /**
   * Starts measuring the time it takes to execute a function. Ensure to call the returned function to end the measurement
   *
   * For example:
   *
   * ```
   * const end = measurePerformanceService.start('label');
   *
   * try {
   *    // Do something
   * } finally {
   *   end();
   * }
   * ```
   * @param label
   * @param startMetadata
   * @returns A function that ends the measurement when called (useful in a finally block)
   */
  start(label: string, startMetadata?: LogMetadata) {
    const id = startMetadata ? `${label}-${generateRandomId()}` : label;

    const startTime = Date.now();
    const value = { start: startTime, startMetadata };

    this.timesPerLabel.set(id, value);

    return (endMetadata?: LogMetadata) => {
      const endTime = Date.now();
      const diff = endTime - startTime;

      this.timesPerLabel.set(id, { ...value, diff, endMetadata });
    };
  }

  /**
   * Returns a function that measures the time it takes to execute the given function
   *
   * For example:
   *
   * ```
   * const fn = measurePerformanceService.observe(() => {
   *  // Do something
   * }, 'label');
   *
   * fn();
   * ```
   * @param fn
   * @param label
   */
  observe<Return>(fn: (...args: any) => Return, label: string) {
    return (...args: any): Return => {
      const end = this.start(label);

      let isPromise = false;

      try {
        const result = fn(...args);

        if (result instanceof Promise) {
          isPromise = true;

          return result.finally(() => end()) as unknown as Return;
        }

        return result;
      } finally {
        if (!isPromise) {
          end();
        }
      }
    };
  }

  /**
   * Measures all methods of the target object
   * @param target
   * @param options
   * @param options.ignoredMethods Methods to ignore
   */
  measureThis(target: any, options?: { ignoredMethods?: string[] }) {
    const ignoredMethods = options?.ignoredMethods ?? [];

    let currentPrototype = Object.getPrototypeOf(target);

    while (currentPrototype && currentPrototype !== Object.prototype) {
      const methodNames = Object.getOwnPropertyNames(currentPrototype).filter(name => {
        try {
          return (
            name !== 'constructor' &&
            !ignoredMethods.includes(name) &&
            typeof target[name] === 'function'
          );
        } catch (e) {
          console.warn(
            `[MeasurePerformanceService] Error while checking property ${name}. If you want to ignore this message, add it to the ignoredMethods array.`,
            e,
          );
          return false;
        }
      });

      methodNames.forEach(methodName => {
        const originalMethod = target[methodName];
        target[methodName] = this.observe(
          originalMethod.bind(target),
          `${this.prefix}.${methodName}`,
        );
      });

      currentPrototype = Object.getPrototypeOf(currentPrototype);
    }
  }
}

/**
 * Decorator to measure the performance of a method
 * @param measurePerformanceService
 */
export const MeasureMethodPerformance = (
  measurePerformanceService: MeasurePerformanceService,
) => {
  return (_: any, propertyKey: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;

    descriptor.value = measurePerformanceService.observe(
      originalMethod,
      `${measurePerformanceService.prefix}.${propertyKey}`,
    );

    return descriptor;
  };
};
